import { useCallback, useEffect, useRef } from 'react';
import { Panel } from '../ui/Panel';
import { btnGhost } from '../ui/styles';
import { fetchCashDayCloses } from '../adminApi';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { money } from '../../core/format/currency';
import { LoadFailed, LoadingLine } from '../ui/LoadState';
import { clock } from './kassaLabels';
import { dayShort, shiftDate, today } from '../../core/format/dates';
import type { CashDayClose } from '../../core/types/kassa';

/** Zoveel dagen terug in het overzicht; ouder staat in de export van Omzet. */
const DAYS_BACK = 31;
type Props = {
  /** Opnieuw laden na een afsluiting: andere sleutel = opnieuw ophalen. */
  version: string;
  onOpenDay: (day: string) => void;
};

/**
 * De dagafsluitingen van de afgelopen maand, alleen voor Sarah: wanneer, door wie (zij of de
 * collega), wat er geteld is en het verschil. Een dag openen toont die dag in de kassa.
 */
export function CashDayCloses({
  version,
  onOpenDay
}: Props) {
  const to = today();
  const from = shiftDate(to, -DAYS_BACK);
  const load = useCallback((opts: {
    signal?: AbortSignal;
  }) => fetchCashDayCloses(from, to, opts), [from, to]);
  const {
    data,
    status,
    reload,
    refresh
  } = useLatestLoad<CashDayClose[]>(load);
  // Na een afsluiting (nieuwe `version`) op de achtergrond verversen; niet bij het eerste tonen.
  const shown = useRef(version);
  useEffect(() => {
    if (shown.current === version) return;
    shown.current = version;
    refresh();
  }, [version, refresh]);
  return <Panel title="Dagafsluitingen" description="De afgelopen maand, met wie de dag afsloot." flush data-id="element-2186" data-mp="xsvoois5a55t">
      {status === 'error' && !data ? <LoadFailed variant="inline" title="De dagafsluitingen laden lukte niet" onRetry={reload} className="px-4 py-4 sm:px-5" data-id="element-2187" data-mp="ssr4aho48qab" /> : !data ? <LoadingLine className="px-4 py-4 sm:px-5" data-id="element-2188" data-mp="jufbc8zs1gep" /> : data.length === 0 ? <p className="px-4 py-4 text-[13px] text-muted sm:px-5" data-id="element-2189" data-mp="euj5033lxcvz">Nog geen dag afgesloten.</p> : <ul className="max-h-80 divide-y divide-line overflow-y-auto" tabIndex={0} aria-label="Dagafsluitingen" data-id="element-2190" data-mp="5773ni5oft7h">
          {data.map(c => <li key={c.day} data-id="element-2191" data-mp="a7uti0i66404">
              <button type="button" onClick={() => onOpenDay(c.day)} className={`${btnGhost} flex w-full items-start justify-between gap-3 rounded-none px-4 py-3 text-left sm:px-5`} data-id="element-2192" data-mp="3reolr6cmsar9">
                <span className="min-w-0" data-id="element-2193" data-mp="a6wf3x3ckes">
                  <span className="block text-[14px] capitalize text-ink" data-id="element-2194" data-mp="nxohkgluok8s">{dayShort(c.day)}</span>
                  <span className="block text-[12px] text-muted" data-id="element-2195" data-mp="sxav6r5lc2i4">
                    {c.closedBy?.name ?? 'Onbekend'} · {clock(c.closedAt)}
                    {c.note ? ` · ${c.note}` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-right" data-id="element-2196" data-mp="j5exvibbwgey">
                  <span className="block text-[13px] tabular-nums text-ink" data-id="element-2197" data-mp="16wk9fla8kmjg">{money(c.countedCashCents)}</span>
                  {c.differenceCents !== 0 && <span className="block text-[12px] tabular-nums text-clay" data-id="element-2198" data-mp="1bpdgwgx3a8z8">
                      {c.differenceCents > 0 ? 'over' : 'tekort'} {money(Math.abs(c.differenceCents))}
                    </span>}
                </span>
              </button>
            </li>)}
        </ul>}
    </Panel>;
}